import React from 'react';
import { Video } from '../../types/video';
import '../../styles/VideoGrid.css';
import imageConfig from '../../config/images';

interface VideoGridProps {
    videos: Video[];
    onVideoSelect?: (videoId: string) => void;
    selectedVideos?: string[];
    onVideoClick?: (videoId: string) => void;
    playingVideo?: string | null; // Add this to track which video is playing
}

const VideoGrid: React.FC<VideoGridProps> = ({ 
    videos, 
    onVideoSelect, 
    selectedVideos = [],
    onVideoClick,
    playingVideo
}) => {
    return (
        <div className="video-grid">
            {videos.map((video) => {
                const isSelected = selectedVideos.includes(video.id);
                const isPlaying = playingVideo === video.id;
                
                return (
                    <div 
                        key={video.id} 
                        className={`video-card ${isSelected ? 'selected' : ''} ${isPlaying ? 'playing' : ''}`}
                        onClick={() => onVideoSelect && onVideoSelect(video.id)}
                    >
                        <div className="video-container">
                            <img 
                                src={video.thumbnailUrl || '/assets/default-thumbnail.jpg'} 
                                alt={video.title} 
                                className="video-thumbnail" 
                            />
                            
                            {/* Watermark overlay - only visible when video is playing */}
                            {isPlaying && (
                                <div className="video-watermark">
                                    <img 
                                        src={imageConfig.watermarkImage || imageConfig.defaultWatermarkImage}
                                        alt="Watermark" 
                                        className="watermark-image" 
                                    />
                                </div>
                            )}
                            
                            {/* Play button icon - make it clickable */}
                            <div 
                                className="video-play-button"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent selection when clicking play
                                    onVideoClick && onVideoClick(video.id);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                            </div>
                        </div>
                        
                        {/* Selection indicator for multi-select mode */}
                        {onVideoSelect && (
                            <div className={`selection-indicator ${isSelected ? 'selected' : ''}`}>
                                {isSelected ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                ) : null}
                            </div>
                        )}
                        
                        <div className="video-info">
                            <div className="video-meta">
                                <p className="video-date">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                    {(() => {
                                        const createdAt = new Date(video.createdAt);
                                        const now = new Date();
                                        const diffMs = now.getTime() - createdAt.getTime();
                                        const diffMins = Math.floor(diffMs / 60000);
                                        const diffHours = Math.floor(diffMs / 3600000);
                                        
                                        if (diffHours < 1) {
                                            return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
                                        } else {
                                            // For videos older than 1 hour, show only hh:mm
                                            const hours = createdAt.getHours();
                                            const minutes = createdAt.getMinutes();
                                            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                                        }
                                    })()}
                                </p>
                                {video.duration && (
                                    <p className="video-duration">
                                        {Math.floor(video.duration / 60)}:
                                        {String(video.duration % 60).padStart(2, '0')}
                                    </p>
                                )}
                            </div>
                            <h3 className="video-title">{video.title}</h3>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default VideoGrid;